import { useSetAtom } from 'jotai';
import {
    uiStateAtomLoginModalAuthSource,
    uiStateAtomLoginModalOpened,
    uiStateAtomLoginModalRedirect,
    uiStateAtomLoginModalTitle,
} from '~/modules-rocco/root/states/ui-state-login-modal-opened';
import type { AuthSource } from '~/modules/user/type';
import { useTracker } from '@archipro-website/tracker';
import { useCallback } from 'react';

/**
 * Custom hook to manage the state of the login modal.
 *
 * This hook provides a function to open or close the login modal and set the authentication source.
 * It also logs the login attempt using the tracker.
 *
 * Opening reports the funnel step twice on purpose: `LoginPromptShown` is the
 * canonical "an auth surface opened" event that every strategy chart keys on,
 * and `accountLogin` is kept beside it because `ClientTeamMembers` reads it.
 * Emitting both here means retiring this primitive changes no chart's numbers —
 * without it, the migration would silently swap which event the top nav, home
 * CTAs, directory banner and 3-page prompt fire.
 */
export const useSetLoginModal = () => {
    const setLoginModalOpened = useSetAtom(uiStateAtomLoginModalOpened);
    const setLoginModalAuthSource = useSetAtom(uiStateAtomLoginModalAuthSource);
    const setLoginModalRedirect = useSetAtom(uiStateAtomLoginModalRedirect);
    const setLoginModalTitle = useSetAtom(uiStateAtomLoginModalTitle);
    const tracker = useTracker();
    const setLoginModal = useCallback(
        ({
            authSource,
            isOpen,
            redirect,
            title,
            skipPromptEvent,
        }: {
            authSource: AuthSource;
            isOpen: boolean;
            redirect?: string;
            title?: string;
            /**
             * Set by callers that already reported this impression themselves —
             * the site-wide 3-page prompt and its sheet's CTA. Without it they
             * would report the same funnel step twice.
             */
            skipPromptEvent?: boolean;
        }) => {
            setLoginModalOpened(isOpen);
            setLoginModalAuthSource(authSource);
            if (redirect) {
                setLoginModalRedirect(redirect);
            }
            setLoginModalTitle(title || '');
            tracker.log('accountLogin', {
                url: new URL(window.location.href),
                data: {
                    step: 1,
                    authSource,
                },
            });
            // Closing is not an impression. Every caller opens today, so this
            // only guards a future one from reporting a prompt nobody saw.
            if (skipPromptEvent || !isOpen) return;
            // Source and ExtraData are the only custom keys the warehouse
            // pipeline persists; the flat key serves network QA.
            tracker.log('LoginPromptShown', {
                url: new URL(window.location.href),
                data: {
                    authSource,
                    Source: authSource,
                    ExtraData: JSON.stringify({ authSource }),
                },
            });
        },
        [
            setLoginModalOpened,
            setLoginModalAuthSource,
            setLoginModalRedirect,
            setLoginModalTitle,
            tracker,
        ],
    );

    return { setLoginModal };
};
